import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { 
  ArrowLeft, Crown, Send, Loader2, MessageSquare, ThumbsUp, ThumbsDown,
  Lightbulb, Flag, Star, Check, Clock, Users, Vote, Plus, ChevronUp, ChevronDown
} from "lucide-react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const ArchitectsPortalPage = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isFounder, setIsFounder] = useState(false);
  const [founderNumber, setFounderNumber] = useState(null);
  const [feedbackList, setFeedbackList] = useState([]);
  const [stats, setStats] = useState(null);
  
  // Tab state
  const [activeTab, setActiveTab] = useState("voting");
  
  // Form state
  const [feedbackType, setFeedbackType] = useState("suggestion");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("medium");
  
  // Voting state
  const [proposals, setProposals] = useState([]);
  const [myProposals, setMyProposals] = useState([]);
  const [showProposalForm, setShowProposalForm] = useState(false);
  const [proposalTitle, setProposalTitle] = useState("");
  const [proposalDescription, setProposalDescription] = useState("");
  const [proposalCategory, setProposalCategory] = useState("feature");
  const [votingStats, setVotingStats] = useState(null);

  const feedbackTypes = [
    { id: "suggestion", label: "Feature Suggestion", icon: Lightbulb, color: "text-amber-500" },
    { id: "bug", label: "Bug Report", icon: Flag, color: "text-red-500" },
    { id: "feedback", label: "General Feedback", icon: MessageSquare, color: "text-blue-500" },
    { id: "praise", label: "What I Love", icon: Star, color: "text-pink-500" }
  ];

  useEffect(() => {
    fetchData();
    fetchVotingData();
  }, [token]);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/architects/portal`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsFounder(response.data.is_founder);
      setFounderNumber(response.data.founder_number);
      setFeedbackList(response.data.feedback || []);
      setStats(response.data.stats);
    } catch (error) {
      console.error("Error fetching portal data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVotingData = async () => {
    try {
      const [proposalsRes, myProposalsRes, statsRes] = await Promise.all([
        axios.get(`${API}/voting/proposals`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/voting/proposals/mine`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/voting/stats`)
      ]);
      setProposals(proposalsRes.data.proposals || []);
      setMyProposals(myProposalsRes.data.proposals || []);
      setVotingStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching voting data:", error);
    }
  };

  const handleVote = async (proposalId, vote) => {
    try {
      const response = await axios.post(
        `${API}/voting/proposals/${proposalId}/vote`,
        { vote },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setProposals(prev => prev.map(p => 
        p.id === proposalId 
          ? { ...p, user_vote: response.data.new_vote, votes_up: response.data.votes_up, votes_down: response.data.votes_down, score: response.data.score }
          : p
      ));
      
      toast.success("Vote recorded!");
    } catch (error) {
      toast.error("Failed to record vote");
    }
  };

  const submitProposal = async () => {
    if (!proposalTitle.trim() || !proposalDescription.trim()) {
      toast.error("Please fill in both title and description");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/voting/proposals`,
        { title: proposalTitle, description: proposalDescription, category: proposalCategory },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Your idea has been submitted for review!");
      setProposalTitle("");
      setProposalDescription("");
      setShowProposalForm(false);
      fetchVotingData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit proposal");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!title.trim() || !description.trim()) {
      toast.error("Please fill in both title and description");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/architects/feedback`, {
        type: feedbackType,
        title,
        description,
        priority
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Thank you for your feedback! Your voice shapes our journey.");
      setTitle("");
      setDescription("");
      setFeedbackType("suggestion");
      setPriority("medium");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit feedback");
    } finally {
      setSubmitting(false);
    }
  };

  const handleFeedbackVote = async (feedbackId) => {
    try {
      await axios.post(`${API}/architects/vote/${feedbackId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      toast.error("Already voted on this item");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Non-founder view
  if (!isFounder) {
    return (
      <div className="min-h-screen bg-background pb-24">
        <header className="bg-gradient-to-r from-amber-500/10 via-yellow-500/10 to-orange-500/10 border-b border-border/50">
          <div className="max-w-4xl mx-auto px-6 py-8">
            <button
              onClick={() => navigate("/dashboard")}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </button>
            
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-yellow-500 flex items-center justify-center">
                <Crown className="w-8 h-8 text-white" strokeWidth={1.5} />
              </div>
              <div>
                <h1 className="font-heading text-3xl font-semibold text-foreground">
                  Architects Portal
                </h1>
                <p className="text-muted-foreground mt-1">
                  Exclusive to Founding Members
                </p>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-6 py-12">
          <Card className="glass-card rounded-3xl border-0 text-center">
            <CardContent className="p-12">
              <div className="w-24 h-24 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mx-auto mb-6">
                <Crown className="w-12 h-12 text-amber-500" />
              </div>
              <h2 className="font-heading text-2xl font-semibold text-foreground mb-4">
                Become a Founding Member
              </h2>
              <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                The Architects Portal is an exclusive space for our first 100 founding members 
                to shape the future of Authentic Self 365. Your voice directly influences what we build next.
              </p>
              <Button onClick={() => navigate("/subscription")} className="rounded-xl">
                <Crown className="w-4 h-4 mr-2" />
                Claim Your Founder Spot
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="bg-gradient-to-r from-amber-500/10 via-yellow-500/10 to-orange-500/10 border-b border-border/50">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </button>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-yellow-500 flex items-center justify-center">
                <Crown className="w-8 h-8 text-white" strokeWidth={1.5} />
              </div>
              <div>
                <h1 className="font-heading text-3xl font-semibold text-foreground">
                  Architects Portal
                </h1>
                <p className="text-muted-foreground mt-1">
                  Welcome, Founding Member #{founderNumber}
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="inline-flex items-center gap-2 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 px-4 py-2 rounded-full">
                <Crown className="w-4 h-4" />
                <span className="font-medium">Architect</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8" data-testid="architects-portal">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-3 gap-4 mb-8">
            <Card className="glass-card rounded-2xl border-0">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-foreground">{stats.total_feedback}</p>
                <p className="text-sm text-muted-foreground">Ideas Shared</p>
              </CardContent>
            </Card>
            <Card className="glass-card rounded-2xl border-0">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-foreground">{stats.implemented}</p>
                <p className="text-sm text-muted-foreground">Implemented</p>
              </CardContent>
            </Card>
            <Card className="glass-card rounded-2xl border-0">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-foreground">{stats.in_progress}</p>
                <p className="text-sm text-muted-foreground">In Progress</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={activeTab === 'voting' ? 'default' : 'outline'}
            onClick={() => setActiveTab('voting')}
            className="rounded-full"
            data-testid="voting-tab"
          >
            <Vote className="w-4 h-4 mr-2" />
            Feature Voting
          </Button>
          <Button
            variant={activeTab === 'feedback' ? 'default' : 'outline'}
            onClick={() => setActiveTab('feedback')}
            className="rounded-full"
            data-testid="feedback-tab"
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            Feedback
          </Button>
        </div>

        {/* Voting Tab */}
        {activeTab === 'voting' && (
          <div className="space-y-6">
            {/* Voting Stats */}
            {votingStats && (
              <div className="grid grid-cols-3 gap-4 mb-6">
                <Card className="glass-card rounded-2xl border-0 bg-gradient-to-br from-teal-50/50 to-emerald-50/50 dark:from-teal-950/20 dark:to-emerald-950/20">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-foreground">{votingStats.approved_proposals}</p>
                    <p className="text-sm text-muted-foreground">Active Proposals</p>
                  </CardContent>
                </Card>
                <Card className="glass-card rounded-2xl border-0 bg-gradient-to-br from-teal-50/50 to-emerald-50/50 dark:from-teal-950/20 dark:to-emerald-950/20">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-foreground">{votingStats.total_votes}</p>
                    <p className="text-sm text-muted-foreground">Total Votes</p>
                  </CardContent>
                </Card>
                <Card className="glass-card rounded-2xl border-0 bg-gradient-to-br from-teal-50/50 to-emerald-50/50 dark:from-teal-950/20 dark:to-emerald-950/20">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-foreground">{votingStats.pending_review}</p>
                    <p className="text-sm text-muted-foreground">Pending Review</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Submit Proposal Button */}
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-heading text-xl font-semibold text-foreground">
                Vote on Features
              </h2>
              <Button
                onClick={() => setShowProposalForm(!showProposalForm)}
                variant={showProposalForm ? "outline" : "default"}
                className="rounded-xl"
              >
                {showProposalForm ? "Cancel" : <><Plus className="w-4 h-4 mr-2" /> Suggest Feature</>}
              </Button>
            </div>

            {/* Proposal Submission Form */}
            {showProposalForm && (
              <Card className="glass-card rounded-2xl border-0 mb-6 border-2 border-primary/20">
                <CardContent className="p-6">
                  <h3 className="font-heading text-lg font-semibold text-foreground mb-4">
                    Suggest a New Feature
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Your idea will be reviewed by the team. Once approved, other Architects can vote on it!
                  </p>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium mb-1 block">Category</label>
                      <div className="flex gap-2">
                        {["feature", "improvement", "content"].map(cat => (
                          <button
                            key={cat}
                            onClick={() => setProposalCategory(cat)}
                            className={`px-4 py-2 rounded-lg text-sm capitalize ${
                              proposalCategory === cat 
                                ? 'bg-primary text-primary-foreground' 
                                : 'bg-muted hover:bg-muted/80'
                            }`}
                          >
                            {cat}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium mb-1 block">Title</label>
                      <Input
                        placeholder="Brief, catchy title for your idea..."
                        value={proposalTitle}
                        onChange={(e) => setProposalTitle(e.target.value)}
                        className="rounded-lg"
                      />
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium mb-1 block">Description</label>
                      <Textarea
                        placeholder="Describe your idea in detail. Why would this be valuable?"
                        value={proposalDescription}
                        onChange={(e) => setProposalDescription(e.target.value)}
                        className="rounded-lg min-h-[100px]"
                      />
                    </div>
                    
                    <Button
                      onClick={submitProposal}
                      disabled={submitting || !proposalTitle.trim() || !proposalDescription.trim()}
                      className="w-full rounded-xl"
                    >
                      {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                      Submit for Review
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Proposals List */}
            {proposals.length === 0 ? (
              <Card className="glass-card rounded-2xl border-0">
                <CardContent className="p-8 text-center">
                  <Vote className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
                    No Proposals Yet
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    Be the first to suggest a feature for the community to vote on!
                  </p>
                  <Button onClick={() => setShowProposalForm(true)} className="rounded-xl">
                    <Plus className="w-4 h-4 mr-2" /> Suggest First Feature
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {proposals.map(proposal => (
                  <Card key={proposal.id} className="glass-card rounded-2xl border-0 overflow-hidden" data-testid={`proposal-${proposal.id}`}>
                    <CardContent className="p-0">
                      <div className="flex">
                        {/* Voting Section */}
                        <div className="flex flex-col items-center justify-center p-4 bg-muted/30 min-w-[80px]">
                          <button
                            onClick={() => handleVote(proposal.id, proposal.user_vote === 1 ? 0 : 1)}
                            className={`p-2 rounded-lg transition-colors ${
                              proposal.user_vote === 1 
                                ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400' 
                                : 'hover:bg-muted text-muted-foreground'
                            }`}
                          >
                            <ChevronUp className="w-6 h-6" />
                          </button>
                          <span className={`text-xl font-bold my-1 ${
                            proposal.score > 0 ? 'text-green-600 dark:text-green-400' : 
                            proposal.score < 0 ? 'text-red-500' : 'text-muted-foreground'
                          }`}>
                            {proposal.score}
                          </span>
                          <button
                            onClick={() => handleVote(proposal.id, proposal.user_vote === -1 ? 0 : -1)}
                            className={`p-2 rounded-lg transition-colors ${
                              proposal.user_vote === -1 
                                ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' 
                                : 'hover:bg-muted text-muted-foreground'
                            }`}
                          >
                            <ChevronDown className="w-6 h-6" />
                          </button>
                        </div>
                        
                        {/* Content Section */}
                        <div className="p-4 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                proposal.category === 'feature' ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400' :
                                proposal.category === 'improvement' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                                'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                              }`}>
                                {proposal.category}
                              </span>
                              <h3 className="font-heading text-lg font-semibold text-foreground mt-1">
                                {proposal.title}
                              </h3>
                            </div>
                          </div>
                          <p className="text-muted-foreground text-sm mt-2">
                            {proposal.description}
                          </p>
                          <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                            <span>by {proposal.submitted_by_name}</span>
                            <span>•</span>
                            <span className="flex items-center gap-1">
                              <ThumbsUp className="w-3 h-3" /> {proposal.votes_up}
                            </span>
                            <span className="flex items-center gap-1">
                              <ThumbsDown className="w-3 h-3" /> {proposal.votes_down}
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* My Proposals */}
            {myProposals.length > 0 && (
              <div className="mt-8">
                <h3 className="font-heading text-lg font-semibold text-foreground mb-4">
                  My Submissions
                </h3>
                <div className="space-y-3">
                  {myProposals.map(p => (
                    <Card key={p.id} className="glass-card rounded-xl border-0">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium text-foreground">{p.title}</h4>
                            <p className="text-sm text-muted-foreground">{p.category}</p>
                          </div>
                          <span className={`text-xs px-3 py-1 rounded-full ${
                            p.status === 'approved' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                            p.status === 'rejected' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                            'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                          }`}>
                            {p.status === 'pending' ? 'Under Review' : p.status}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Feedback Tab */}
        {activeTab === 'feedback' && (
          <>
        {/* Submit Feedback Form */}
        <Card className="glass-card rounded-3xl border-0 mb-8">
          <CardContent className="p-8">
            <h2 className="font-heading text-xl font-semibold text-foreground mb-6">
              Share Your Vision
            </h2>
            
            {/* Feedback Type */}
            <div className="mb-6">
              <label className="text-sm font-medium mb-3 block">What would you like to share?</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {feedbackTypes.map(type => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.id}
                      onClick={() => setFeedbackType(type.id)}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        feedbackType === type.id 
                          ? 'border-primary bg-primary/5' 
                          : 'border-transparent bg-muted/50 hover:bg-muted'
                      }`}
                    >
                      <Icon className={`w-6 h-6 mx-auto mb-2 ${type.color}`} />
                      <p className="text-sm font-medium">{type.label}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Title */}
            <div className="mb-4">
              <label className="text-sm font-medium mb-1 block">Title</label>
              <Input
                placeholder="Brief summary of your idea..."
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="rounded-lg"
              />
            </div>

            {/* Description */}
            <div className="mb-4">
              <label className="text-sm font-medium mb-1 block">Description</label>
              <Textarea
                placeholder="Tell us more about your idea, the problem it solves, or how it would help your journey..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="rounded-lg min-h-[120px]"
              />
            </div>

            {/* Priority (for bugs) */}
            {feedbackType === "bug" && (
              <div className="mb-6">
                <label className="text-sm font-medium mb-2 block">Priority</label>
                <div className="flex gap-3">
                  {["low", "medium", "high"].map(p => (
                    <button
                      key={p}
                      onClick={() => setPriority(p)}
                      className={`px-4 py-2 rounded-lg capitalize transition-colors ${
                        priority === p 
                          ? p === 'high' ? 'bg-red-500 text-white' 
                            : p === 'medium' ? 'bg-amber-500 text-white'
                            : 'bg-green-500 text-white'
                          : 'bg-muted hover:bg-muted/80'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <Button 
              onClick={handleSubmit} 
              disabled={submitting || !title.trim() || !description.trim()}
              className="rounded-xl"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Submit Feedback
            </Button>
          </CardContent>
        </Card>

        {/* Community Ideas */}
        <Card className="glass-card rounded-3xl border-0">
          <CardContent className="p-8">
            <h2 className="font-heading text-xl font-semibold text-foreground mb-6">
              Community Ideas
            </h2>
            
            {feedbackList.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                Be the first to share an idea with the community!
              </p>
            ) : (
              <div className="space-y-4">
                {feedbackList.map((item, idx) => {
                  const typeInfo = feedbackTypes.find(t => t.id === item.type) || feedbackTypes[0];
                  const Icon = typeInfo.icon;
                  
                  return (
                    <div key={item.id || idx} className="bg-muted/30 rounded-2xl p-5">
                      <div className="flex items-start gap-4">
                        <button
                          onClick={() => handleFeedbackVote(item.id)}
                          className={`flex flex-col items-center gap-1 p-2 rounded-lg transition-colors ${
                            item.user_voted ? 'bg-primary/20 text-primary' : 'hover:bg-muted'
                          }`}
                        >
                          <ThumbsUp className={`w-5 h-5 ${item.user_voted ? 'fill-current' : ''}`} />
                          <span className="text-sm font-bold">{item.votes || 0}</span>
                        </button>
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Icon className={`w-4 h-4 ${typeInfo.color}`} />
                            <span className="text-xs text-muted-foreground capitalize">{item.type}</span>
                            {item.status && (
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                item.status === 'implemented' ? 'bg-green-100 text-green-700' :
                                item.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                                item.status === 'planned' ? 'bg-emerald-100 text-teal-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {item.status === 'in_progress' ? 'In Progress' : item.status}
                              </span>
                            )}
                          </div>
                          <h3 className="font-medium text-foreground mb-1">{item.title}</h3>
                          <p className="text-sm text-muted-foreground">{item.description}</p>
                          <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                            <span>Founder #{item.founder_number || '?'}</span>
                            <span>•</span>
                            <span>{new Date(item.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
          </>
        )}
      </main>
    </div>
  );
};

export default ArchitectsPortalPage;
